import { useQuery } from '@tanstack/react-query';

import type { AttachmentDto, ResourceTypeDto } from '@/api/generated';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';

type Req = {
  resourceId: string;
  resourceType: ResourceTypeDto;
};

type Return = {
  files: AttachmentDto[];
  isGetting: boolean;
  isUpdating: boolean;
};

export const useGetFiles = (req: Req): Return => {
  const { attachmentsApi } = useApiClient();
  const { userId } = useUserContext();

  const { data, isFetching, isPending } = useQuery<AttachmentDto[]>({
    queryFn: async () => {
      const { data } = await attachmentsApi.getAttachments(
        userId,
        req.resourceId,
        req.resourceType,
      );
      return data;
    },
    queryKey: [QueryKeyEnum.FILES, req.resourceId, req.resourceType],
    select: files => {
      return [...new Map(files.map(file => [file.name + '_' + file.size, file])).values()];
    },
    staleTime: 1000 * 10,
  });

  return {
    files: data ?? [],
    isGetting: isPending,
    isUpdating: isFetching,
  };
};
